import { Mutex } from 'async-mutex';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { v4 as uuidV4 } from 'uuid';
import { ACCESS_TOKEN, CURRENT_MRO, REFRESH_TOKEN } from './../../../constants/local-storage';
import { TListener, TSetSessionParams, TTokenSession } from './token.types';

/**
 * Service to manage tokens for auth
 */
class TokenService {
  private session: TTokenSession | null = null;
  private listeners: Record<string, TListener> = {};
  private refreshLock = new Mutex();
  private updateLock = new Mutex();
  private static storageKeys = [ACCESS_TOKEN, REFRESH_TOKEN, CURRENT_MRO];

  /**
   * Initialize session
   */
  constructor() {
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    window.addEventListener('storage', this.storageListener);

    if (accessToken && refreshToken) {
      this.setSession({ accessToken, refreshToken });
    }
  }

  storageListener = (e: StorageEvent) => {
    if (e.key && TokenService.storageKeys.includes(e.key) && e.newValue !== e.oldValue) {
      const accessToken = localStorage.getItem(ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);

      if (accessToken && refreshToken) {
        this.setSession({ accessToken, refreshToken });
      } else if (!accessToken && !refreshToken) {
        this.revokeSession();
      }
    }
  };

  /**
   * Set tokens after login....
   * @param session session
   */
  public setSession = (session: TSetSessionParams | null) => {
    return this.updateLock.runExclusive(() => {
      if (!session) {
        this.revokeSession();
        return null;
      }

      const finalSession: TTokenSession = {
        ...session,
        decodedUserToken: jwtDecode(session.accessToken),
      };

      this.session = finalSession;

      localStorage.setItem(ACCESS_TOKEN, this.session.accessToken);
      localStorage.setItem(REFRESH_TOKEN, this.session.refreshToken);
      localStorage.setItem(CURRENT_MRO, finalSession.decodedUserToken.mro);

      this.notifyListeners();

      return this.session;
    });
  };

  /**
   * Get users session
   */
  public getSession = async () => {
    /**
     * If session is updating, wait for update
     */
    if (this.updateLock.isLocked()) {
      await this.updateLock.waitForUnlock();
    }

    /**
     * If session is updating, wait for update
     */
    if (this.refreshLock.isLocked()) {
      await this.refreshLock.waitForUnlock();
    }

    return this.session;
  };

  /**
   * Get users session without waiting for updates
   */
  public getSessionDangerously = () => {
    return this.session;
  };

  /**
   * Refresh access token if possible
   * @returns  new tokens
   */
  refreshAccessToken = async () => {
    // eslint-disable-next-line
    console.info('[TokenService]: Refreshing token');

    /**
     * If refreshing wait for ongoing request,
     * and return new session
     */
    if (this.refreshLock.isLocked()) {
      await this.refreshLock.waitForUnlock();

      if (!this.session) {
        throw new Error('REFRESH_TOKEN_ERROR');
      }

      return this.session!;
    }

    /**
     * Start refresh and wait to finish.
     * After that clean promise and return tokens
     * Or clear auth
     */
    try {
      const oldTokens = await this.getSession();

      await this.refreshLock.runExclusive(async () => {
        if (!oldTokens || !oldTokens.refreshToken) {
          throw new Error("Can't refresh tokens");
        }

        const { data } = await axios.post<{
          accessToken: string;
          refreshToken: string;
          companyToken?: string;
        }>(
          `${process.env.REACT_APP_NEW_AUTH_API_URL}/v1/refresh-token`,
          {
            refreshToken: oldTokens.refreshToken,
          },
          {
            headers: {
              Authorization: `Bearer ${oldTokens.accessToken}`,
            },
          },
        );

        await this.setSession(data);
        return data;
      });

      return this.session!;
    } catch (error) {
      this.revokeSession();
      throw error;
    }
  };

  /**
   * remove all data from session
   */
  public revokeSession = () => {
    this.session = null;
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(CURRENT_MRO);
    this.notifyListeners();
  };

  /**
   * Add listener for change
   * @param listener listener cb
   * @returns unregister cb
   */
  public addEventListener = (listener: TListener) => {
    const id = uuidV4();
    this.listeners[id] = listener;

    return () => this.removeEventListener(id);
  };

  /**
   * Remove listener
   * @param id id of listener
   */
  private removeEventListener = (id: string) => {
    delete this.listeners[id];
  };

  /**
   * Notify listeners about change
   */
  private notifyListeners = () => {
    for (const listener of Object.values(this.listeners)) {
      listener(this.session);
    }
  };
}

const tokenService = new TokenService();
export default tokenService;
